import { NavBarRoute } from "#components/nav-bars-shared/nav-bar";
import { NavBarRouteV2 } from "#components/nav-bars/navigation-with-permissions";
import * as React from "react";
import { browserHistory } from "react-router";

type NavItem =
	| Pick<NavBarRoute, "url" | "label">
	| Pick<NavBarRouteV2, "url" | "label" | "alternativeUrls">;

interface IBreadCrumb {
	currentPage: string;
	onChangeCurrentPage(currentPage: string): void;
	onChangeNavBarList(navBarList: NavItem[]): void;
}

let lastPathname = window.location.pathname;

export const BreadCrumbContext = React.createContext({} as IBreadCrumb);

export function BreadCrumbContextProvider({ children }: { children: React.ReactNode }) {
	const [currentPage, setCurrentPage] = React.useState<string>("");
	const [navBarList, setNavBarList] = React.useState<NavItem[]>([]);

	const findExactPathOccurrence = (list: NavItem[]) => {
		const currentPath = window.location.pathname;
		const currentPathWithHash = window.location.pathname + window.location.hash;

		// Exact match prioritário
		for (const item of list) {
			// Verifica URL principal
			if (item.url && (item.url === currentPath || item.url === currentPathWithHash)) {
				return item;
			}

			// Verifica URLs alternativas
			if ("alternativeUrls" in item && item.alternativeUrls) {
				for (const altUrl of item.alternativeUrls) {
					if (altUrl === currentPath || altUrl === currentPathWithHash) {
						return item;
					}
				}
			}
		}

		// Fallback para matching parcial
		const path = currentPath.split("/");
		const lastSegment = path[path.length - 1];

		const occurrencesFound = list.filter(item => {
			// Verifica URL principal
			if (item.url?.includes(lastSegment)) {
				return true;
			}

			// Verifica URLs alternativas
			if ("alternativeUrls" in item && item.alternativeUrls) {
				return item.alternativeUrls.some(altUrl => altUrl.includes(lastSegment));
			}

			return false;
		});

		if (occurrencesFound.length > 1) {
			const foundItem = occurrencesFound.find(item => {
				const splitItemPath = item.url?.split("/");
				const lastCurrPath = path[path.length - 1];
				const lastItemPath = splitItemPath?.[splitItemPath.length - 1]?.split("#")[0];

				return lastItemPath === lastCurrPath;
			});

			return foundItem || occurrencesFound[0];
		}

		return occurrencesFound[0];
	};

	const onRouteChange = React.useCallback(() => {
		if (navBarList.length) {
			const foundItem = findExactPathOccurrence(navBarList);

			if (!foundItem) {
				return;
			}

			setCurrentPage(foundItem.label);
			lastPathname = window.location.pathname;
		}
	}, [navBarList, setCurrentPage]);

	browserHistory.listen(({ pathname }) => {
		if (lastPathname !== pathname) {
			onRouteChange();
		}
	});

	React.useEffect(() => {
		onRouteChange();
	}, [navBarList]);

	function onChangeCurrentPage(currentPage: string) {
		setCurrentPage(currentPage);
	}

	function onChangeNavBarList(navBarList: NavItem[]) {
		setNavBarList([...navBarList]);
	}

	return (
		<BreadCrumbContext.Provider
			value={{ currentPage, onChangeCurrentPage, onChangeNavBarList }}
		>
			{children}
		</BreadCrumbContext.Provider>
	);
}

export function useBreadcrumb() {
	return React.useContext(BreadCrumbContext);
}
